"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { BarChart3, ChevronLeft, ChevronRight, MessageSquare, Shield, Sparkles, TrendingUp, type LucideIcon } from "lucide-react";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { WebsiteCtaLink } from "@/app/(website)/_components/website-cta-link";
import { websiteBodyText, websiteCardTitleText, websiteEyebrowText, websiteSectionTitleText } from "@/app/(website)/_components/website-type-scale";

const AUTO_ADVANCE_MS = 10000;

type AgentCard = {
  id: string;
  name: string;
  label: string;
  oneLiner: string;
  tooltip: string;
  image: string;
  accent: string;
  accentSoft: string;
  icon: LucideIcon;
};

const agentCards: AgentCard[] = [
  {
    id: "explorer",
    name: "Explorer",
    label: "Finds what deserves attention",
    oneLiner: "Surfaces the patterns and oddities you would otherwise miss.",
    tooltip: "Keeps scanning for hidden shifts, unusual patterns, and the next question worth asking.",
    image: "/website/images/agents/explorer.png",
    accent: "#ec4899",
    accentSoft: "rgba(236,72,153,0.22)",
    icon: Sparkles,
  },
  {
    id: "analyst",
    name: "Analyst",
    label: "Turns questions into answers",
    oneLiner: "Gets to the point fast and stays grounded in real store data.",
    tooltip: "Understands the question, reaches into your data, and comes back with something you can trust.",
    image: "/website/images/agents/analyst.png",
    accent: "#38bdf8",
    accentSoft: "rgba(56,189,248,0.22)",
    icon: MessageSquare,
  },
  {
    id: "manager",
    name: "Manager",
    label: "Points to what matters now",
    oneLiner: "Interprets the signal and helps you decide what deserves action first.",
    tooltip: "Turns movement in the business into priorities, not just more numbers to inspect.",
    image: "/website/images/agents/manager.png",
    accent: "#facc15",
    accentSoft: "rgba(250,204,21,0.22)",
    icon: TrendingUp,
  },
  {
    id: "compare",
    name: "Compare",
    label: "Tracks how the market moves",
    oneLiner: "Watches competitors, pricing shifts, and positioning changes around your store.",
    tooltip: "Keeps an eye on competitor moves so you can react before the gap gets expensive.",
    image: "/website/images/agents/architect.png",
    accent: "#a855f7",
    accentSoft: "rgba(168,85,247,0.22)",
    icon: BarChart3,
  },
  {
    id: "Reporter",
    name: "Reporter",
    label: "Briefs you without the noise",
    oneLiner: "Pulls the whole ecosystem together into something calm, clear, and useful.",
    tooltip: "Connects your store signals into one daily brief so you know what changed and what to do next.",
    image: "/website/images/agents/sentinal.png",
    accent: "#22c55e",
    accentSoft: "rgba(34,197,94,0.22)",
    icon: Shield,
  },
];

function getFanStyle(index: number, activeIndex: number) {
  const total = agentCards.length;
  const offset = (index - activeIndex + total) % total;
  const spreadIndex = offset > total / 2 ? offset - total : offset;
  const absIndex = Math.abs(spreadIndex);

  return {
    transform: `translate(-50%, -50%) translateX(${spreadIndex * 38}px) translateY(${absIndex * 12}px) rotate(${spreadIndex * 5}deg) scale(${index === activeIndex ? 1.02 : 0.94 - absIndex * 0.06})`,
    zIndex: total - absIndex,
    opacity: absIndex > 1 ? 0 : index === activeIndex ? 1 : 0.34,
  };
}

function getDesktopFanStyle(index: number, activeIndex: number) {
  const total = agentCards.length;
  const offset = (index - activeIndex + total) % total;
  const spreadIndex = offset > total / 2 ? offset - total : offset;
  const absIndex = Math.abs(spreadIndex);

  return {
    transform: `translate(-50%, -50%) translateX(${spreadIndex * 100}px) translateY(${absIndex * 30}px) rotate(${spreadIndex * 8}deg) scale(${index === activeIndex ? 1.06 : 0.93 - absIndex * 0.05})`,
    zIndex: total - absIndex,
    opacity: absIndex > 2 ? 0 : index === activeIndex ? 1 : 0.82 - absIndex * 0.12,
  };
}

export function UndercoverAgentsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHoverPaused, setIsHoverPaused] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<{ startX: number; isDragging: boolean; hasDragged: boolean }>({
    startX: 0,
    isDragging: false,
    hasDragged: false,
  });

  useEffect(() => {
    const updateViewport = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);

    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    if (isHoverPaused) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setActiveIndex((current) => (current + 1) % agentCards.length);
    }, AUTO_ADVANCE_MS);

    return () => window.clearTimeout(timeout);
  }, [activeIndex, isHoverPaused]);

  const cycleAgent = (direction: "prev" | "next") => {
    setActiveIndex((current) => {
      if (direction === "next") {
        return (current + 1) % agentCards.length;
      }

      return (current - 1 + agentCards.length) % agentCards.length;
    });
  };

  const selectAgent = (index: number) => {
    setActiveIndex(index);
  };

  const handleSwipeDelta = (delta: number) => {
    if (Math.abs(delta) < 48) {
      return;
    }

    cycleAgent(delta < 0 ? "next" : "prev");
  };

  const isNavigationTarget = (target: EventTarget | null) => {
    return target instanceof HTMLElement && target.closest("[data-agent-nav='true']") !== null;
  };

  const startDrag = (clientX: number) => {
    dragStateRef.current = {
      startX: clientX,
      isDragging: true,
      hasDragged: false,
    };
    setIsDragging(true);
    setDragOffset(0);
  };

  const updateDrag = (clientX: number) => {
    if (!dragStateRef.current.isDragging) {
      return;
    }

    const delta = clientX - dragStateRef.current.startX;
    dragStateRef.current.hasDragged = dragStateRef.current.hasDragged || Math.abs(delta) > 8;
    setDragOffset(Math.max(-120, Math.min(120, delta * 0.55)));
  };

  const finishDrag = (clientX?: number) => {
    if (!dragStateRef.current.isDragging) {
      return;
    }

    const delta = typeof clientX === "number" ? clientX - dragStateRef.current.startX : 0;
    dragStateRef.current.isDragging = false;
    setIsDragging(false);
    setDragOffset(0);
    handleSwipeDelta(delta);
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isNavigationTarget(event.target)) {
      return;
    }

    event.preventDefault();
    startDrag(event.clientX);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    updateDrag(event.clientX);
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    finishDrag(event.clientX);
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (isNavigationTarget(event.target)) {
      return;
    }

    const touch = event.touches[0];

    if (!touch) {
      return;
    }

    startDrag(touch.clientX);
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!dragStateRef.current.isDragging) {
      return;
    }

    const touch = event.touches[0];

    if (!touch) {
      return;
    }

    event.preventDefault();
    updateDrag(touch.clientX);
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!dragStateRef.current.isDragging) {
      return;
    }

    const touch = event.changedTouches[0];

    if (!touch) {
      finishDrag();
      return;
    }

    finishDrag(touch.clientX);
  };

  const activeAgent = agentCards[activeIndex];
  const ctaBackgroundColor = activeAgent.id === "manager" ? "#f59e0b" : activeAgent.accent;
  const ctaShadowColor = activeAgent.id === "manager" ? "rgba(245,158,11,0.28)" : activeAgent.accentSoft;
  const ctaTextClassName = activeAgent.id === "manager" ? "text-slate-950 hover:text-slate-950 active:text-slate-950" : "text-white hover:text-white active:text-white";

  return (
    <section className="relative overflow-hidden bg-slate-900/45 py-24">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_18%_18%,rgba(168,85,247,0.1),transparent_20%),radial-gradient(circle_at_82%_28%,rgba(56,189,248,0.1),transparent_18%)]" />
      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid items-center gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:gap-10">
          <ScrollReveal>
            <div className="mx-auto max-w-lg text-center lg:mx-0 lg:text-left">
              <h2 className={`${websiteSectionTitleText} text-white`}>
                Your <span className="bg-gradient-to-r from-pink-300 via-white to-cyan-300 bg-clip-text text-transparent">undercover agents</span>
              </h2>
              <p className={`mt-5 text-slate-300 ${websiteBodyText}`}>
                Specialized ecommerce agents quietly investigating your store, comparing the market, and handing you the next move before the chaos reaches you.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <div
              className={`relative mx-auto h-[31rem] w-full max-w-[19rem] select-none touch-pan-y overflow-visible md:h-[40rem] md:max-w-[46rem] lg:-ml-12 ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
              onMouseEnter={() => setIsHoverPaused(true)}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={() => {
                setIsHoverPaused(false);
                finishDrag();
              }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={() => {
                finishDrag();
              }}
            >
              <div className="absolute left-1/2 top-1/2 h-[76%] w-[76%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-purple-500/10 blur-[120px]" />

              <button
                type="button"
                data-agent-nav="true"
                onMouseDown={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
                onClick={() => cycleAgent("prev")}
                className="absolute -left-6 top-1/2 z-30 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-slate-700/60 bg-slate-950/88 text-white shadow-[0_18px_40px_rgba(2,6,23,0.36)] backdrop-blur-md transition-[transform,background-color,border-color] duration-300 hover:scale-105 hover:border-slate-500/70 hover:bg-slate-900 md:left-6 md:h-12 md:w-12"
                aria-label="Show previous agent"
              >
                <ChevronLeft className="h-4 w-4 text-white md:h-5 md:w-5" />
              </button>

              <button
                type="button"
                data-agent-nav="true"
                onMouseDown={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
                onClick={() => cycleAgent("next")}
                className="absolute -right-6 top-1/2 z-30 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-slate-700/60 bg-slate-950/88 text-white shadow-[0_18px_40px_rgba(2,6,23,0.36)] backdrop-blur-md transition-[transform,background-color,border-color] duration-300 hover:scale-105 hover:border-slate-500/70 hover:bg-slate-900 md:right-6 md:h-12 md:w-12"
                aria-label="Show next agent"
              >
                <ChevronRight className="h-4 w-4 text-white md:h-5 md:w-5" />
              </button>

              <div
                className={`absolute left-1/2 top-1/2 h-full w-full -translate-x-1/2 -translate-y-1/2 ${isDragging ? "transition-none" : "transition-transform duration-300 ease-out"}`}
                style={{ transform: `translate(-50%, -50%) translateX(${dragOffset}px)` }}
              >
                {agentCards.map((agent, index) => {
                  const Icon = agent.icon;
                  const isActive = index === activeIndex;
                  const fanStyle = isMobileViewport ? getFanStyle(index, activeIndex) : getDesktopFanStyle(index, activeIndex);

                  return (
                    <button
                      key={agent.id}
                      type="button"
                      draggable={false}
                      onDragStart={(event) => event.preventDefault()}
                      onClick={(event) => {
                        if (dragStateRef.current.hasDragged) {
                          event.preventDefault();
                          dragStateRef.current.hasDragged = false;
                          return;
                        }

                        selectAgent(index);
                      }}
                      className={`absolute left-1/2 top-1/2 h-[26rem] w-[16.5rem] origin-center -translate-x-1/2 -translate-y-1/2 select-none text-left ${isDragging ? "transition-none" : "transition-all duration-700 ease-out"} md:h-[32rem] md:w-[23rem]`}
                      style={fanStyle}
                    >
                      <article className={`relative flex h-full flex-col overflow-hidden rounded-[2.2rem] bg-slate-950 p-4 shadow-[0_28px_100px_rgba(2,6,23,0.5)] ${isDragging ? "transition-none" : "transition-all duration-500"} ${isActive ? "ring-1 ring-white/12" : ""} md:p-5`}>
                      <div className="absolute inset-x-6 top-0 h-24 rounded-full blur-3xl" style={{ backgroundColor: agent.accentSoft }} />
                      {isActive ? (
                        <div key={`stars-${activeIndex}`} className="pointer-events-none absolute inset-0 z-20 overflow-hidden">
                          {[
                            { left: "14%", top: "20%", delay: "0ms", size: "h-1.5 w-1.5" },
                            { left: "26%", top: "30%", delay: "120ms", size: "h-1 w-1" },
                            { left: "44%", top: "16%", delay: "210ms", size: "h-1.5 w-1.5" },
                            { left: "74%", top: "24%", delay: "160ms", size: "h-1.5 w-1.5" },
                            { left: "20%", top: "68%", delay: "280ms", size: "h-1 w-1" },
                            { left: "54%", top: "74%", delay: "380ms", size: "h-1.5 w-1.5" },
                            { left: "76%", top: "60%", delay: "240ms", size: "h-1 w-1" },
                          ].map((spark, sparkIndex) => (
                            <span
                              key={`${activeIndex}-${sparkIndex}`}
                              className={`absolute rounded-full bg-white/90 ${spark.size} animate-[agent-star-burst_900ms_ease-out_forwards]`}
                              style={{
                                left: spark.left,
                                top: spark.top,
                                animationDelay: spark.delay,
                                boxShadow: `0 0 14px ${agent.accent}`,
                              }}
                            />
                          ))}
                        </div>
                      ) : null}
                      <div className="relative flex items-center justify-between">
                        <div className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-200">
                          <Icon className="h-3.5 w-3.5" style={{ color: agent.accent }} />
                          <span>{agent.name}</span>
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Agent</span>
                      </div>

                        <div className="relative mt-4 h-[12rem] overflow-hidden rounded-[1.8rem] bg-slate-900 md:mt-5 md:h-[18rem]">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_18%,rgba(255,255,255,0.06),transparent_36%)]" />
                          <Image src={agent.image} alt={agent.name} fill draggable={false} className={`select-none object-contain p-4 ${isDragging ? "transition-none" : "transition-all duration-700"} ${isActive ? "scale-105" : "scale-[0.97]"}`} />
                      </div>

                      <div className="relative mt-4 min-h-[6.5rem] text-center md:mt-5 md:min-h-[7rem] md:text-left">
                        <h3 className={`${websiteCardTitleText} text-white`}>{agent.name}</h3>
                        <p className={`${websiteBodyText} mt-3 text-[0.95rem] leading-relaxed text-slate-300 md:text-base`}>{agent.oneLiner}</p>
                      </div>
                        </article>
                      </button>
                    );
                  })}
                </div>
            </div>
          </ScrollReveal>
        </div>

        <div className="mt-10 flex justify-center">
          <WebsiteCtaLink
            href="/login"
            className={ctaTextClassName}
            style={{ backgroundColor: ctaBackgroundColor, boxShadow: `0 14px 40px ${ctaShadowColor}` }}
          >
            Start Now
          </WebsiteCtaLink>
        </div>
      </div>

      <style jsx>{`
        @keyframes agent-star-burst {
          0% {
            opacity: 0;
            transform: scale(0.2) translateY(10px);
          }

          35% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }

          100% {
            opacity: 0;
            transform: scale(1.8) translateY(-18px);
          }
        }
      `}</style>
    </section>
  );
}
